import { SeawardException } from '@ch-types/SeawardExceptions';
import { IArgumentType } from '@ch-types/Exception';

/**
 * Base exception for all camera capture exceptions.
 */
export class CameraException extends SeawardException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'CameraException';
    }

    /**
     * Reconstructs this exception type from its serialized cross-frame form.
     *
     * An instance method by design, not a static one: `ATBaseClient.translate()` dispatches it off
     * the registered constructor's `prototype` after matching the incoming `model`, so no instance
     * exists at call time and `this` is unused.
     *
     * @param obj - Containing message and other data.
     */
    public deserialize(obj): CameraException {
        return new CameraException({
            message: obj.message,
        });
    }
}

/**
 * Exception meaning the batch capture API was reached but the environment behind it cannot serve
 * it — `CameraCaptureService.captureImagesAndUpload` against a host frame that predates the
 * `cameraBatchCapture` listener.
 *
 * Nothing in this package raises it any more. The cross-frame adapter implements batch capture
 * (STARSHIP-134) and Velocity rejects with `UnsupportedEnvironmentException` instead, so every
 * instance now arrives ACROSS a frame from an older IhmAssociateToolsBase. It is registered in the
 * `ATBaseClient` constructor for exactly that reason: `translate()` looks the incoming `model` up
 * in `objectMap` and would hand back a raw object on a miss, so without the registration
 * `error instanceof BatchCaptureNotImplementedException` would silently stop holding.
 *
 * This is a CAPABILITY error, not a capture error, and the difference matters. A real batch
 * session RESOLVES even when some photos fail (their positions arrive in
 * `BatchPhotoUploadResult.failedIndices`); it does not reject. So code that treats a rejection as
 * "some photos failed" will be wrong.
 *
 * `CameraCaptureService.isBatchCaptureImplemented()` does not predict this one — it describes this
 * library's adapter, not the host's age, so an old host passes the gate. Gate on the flag for the
 * no-camera-bridge case and handle this rejection for the old-host case.
 */
export class BatchCaptureNotImplementedException extends CameraException {
    /* istanbul ignore next */
    constructor(args: IArgumentType) {
        super(args);
        this.model = 'BatchCaptureNotImplementedException';
    }

    /**
     * Reconstructs this exception type from its serialized cross-frame form.
     *
     * An instance method by design, not a static one — see the note on
     * {@link CameraException.deserialize}.
     *
     * @param obj - Containing message and other data.
     */
    public deserialize(obj): BatchCaptureNotImplementedException {
        return new BatchCaptureNotImplementedException({
            message: obj.message,
        });
    }
}
